// Subscribe modal — replicates the v5 mockup at access-preview-v5.html
// lines 2975–3005.
//
// Two flavors:
//   - "create"  — opens from the Audit page Subscribe button. Pre-populates
//                 the filter spec from the current page filters (when
//                 "Current filtered view" is selected; "All events" emits
//                 an empty spec).
//   - "edit"    — opens from the Subscriptions list page. Pre-populates
//                 every field from the existing row and PATCHes on submit.
//                 The "What to include" radio is hidden in this flavor —
//                 the row's filter spec is preserved verbatim. (Toggling
//                 between filtered/all on edit is out of scope for Phase 6
//                 because there's nothing on the Subscriptions page to
//                 source a "current filtered view" from.)
//
// Email delivery is OUT OF SCOPE for Phase 6: the runner Lambda that
// renders + ships the CSV via SES is deferred (see banner on
// Subscriptions.tsx). This modal only stores the row.

import { useEffect, useMemo, useState } from "react";
import { ApiError, useApiErrorHandler } from "@/api";
import { Button, Modal, useToast } from "@/components";
import {
  auditClient,
  type AuditSubscription,
  type AuditSubscriptionCadence,
  type AuditSubscriptionFilterSpec,
} from "@/data/clients/auditClient";
import "./Audit.css";

export type SubscribeModalMode = "create" | "edit";

export interface SubscribeModalProps {
  open: boolean;
  onClose: () => void;
  /** Called with the freshly-stored row (created or updated) on success. */
  onSaved: (row: AuditSubscription) => void;
  /** Default email — typically `${alias}@amazon.com`. */
  defaultEmail: string;
  /**
   * Filter spec for "Current filtered view". Required for create mode;
   * unused in edit mode (the row's own spec is rendered).
   */
  currentFilterSpec?: AuditSubscriptionFilterSpec;
  /** Existing row to edit. Required when mode === "edit". */
  existing?: AuditSubscription;
  mode?: SubscribeModalMode;
}

const CADENCE_OPTIONS: ReadonlyArray<{
  value: AuditSubscriptionCadence;
  label: string;
}> = [
  { value: "DAILY", label: "Daily" },
  { value: "WEEKLY", label: "Weekly" },
  { value: "MONTHLY", label: "Monthly" },
];

type IncludeChoice = "filtered" | "all";

function describeCadence(cadence: AuditSubscriptionCadence): string {
  switch (cadence) {
    case "DAILY":
      return "every day at 09:00 UTC";
    case "WEEKLY":
      return "every Monday 09:00 UTC";
    case "MONTHLY":
      return "the 1st of each month at 09:00 UTC";
  }
}

function describeFilters(spec: AuditSubscriptionFilterSpec): string {
  const bits: string[] = [];
  if (spec.location_ids && spec.location_ids.length > 0) {
    bits.push(spec.location_ids.join(", "));
  }
  if (spec.event_types && spec.event_types.length > 0) {
    bits.push(spec.event_types.join(", "));
  }
  if (spec.actor_aliases && spec.actor_aliases.length > 0) {
    bits.push(spec.actor_aliases.join(", "));
  }
  if (spec.q && spec.q.length > 0) {
    bits.push(`"${spec.q}"`);
  }
  return bits.length === 0 ? "all events" : bits.join(" · ");
}

export function SubscribeModal(props: SubscribeModalProps): JSX.Element {
  const {
    open,
    onClose,
    onSaved,
    defaultEmail,
    currentFilterSpec,
    existing,
    mode = "create",
  } = props;

  const toast = useToast();
  const handleApiError = useApiErrorHandler();

  // ---------- form state ----------
  const [cadence, setCadence] = useState<AuditSubscriptionCadence>(
    existing?.cadence ?? "WEEKLY",
  );
  const [include, setInclude] = useState<IncludeChoice>(
    // For edit mode the include radio is hidden; "filtered" reuses
    // existing.filter_spec.
    "filtered",
  );
  const [email, setEmail] = useState<string>(
    existing?.email_to ?? defaultEmail,
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state every time the modal opens so a stale form doesn't bleed
  // through. Rebuilds defaults from props at open-time.
  useEffect(() => {
    if (!open) return;
    setCadence(existing?.cadence ?? "WEEKLY");
    setInclude("filtered");
    setEmail(existing?.email_to ?? defaultEmail);
    setSubmitting(false);
    setError(null);
  }, [open, existing, defaultEmail]);

  // ---------- derived ----------
  const filterSpec: AuditSubscriptionFilterSpec = useMemo(() => {
    if (mode === "edit" && existing) return existing.filter_spec;
    if (include === "all") return {};
    return currentFilterSpec ?? {};
  }, [mode, existing, include, currentFilterSpec]);

  const filterSummary = useMemo(() => describeFilters(filterSpec), [filterSpec]);
  const cadenceSummary = describeCadence(cadence);

  const emailValid = email.includes("@") && email.length >= 3;
  const canSubmit = !submitting && emailValid;

  // ---------- submit ----------
  async function handleSubmit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      let row: AuditSubscription;
      if (mode === "edit" && existing) {
        row = await auditClient.updateSubscription(existing.id, {
          cadence,
          email_to: email,
        });
        toast.success({
          title: "Subscription updated",
          description: `${describeCadence(cadence)} · ${email}`,
        });
      } else {
        row = await auditClient.createSubscription({
          cadence,
          email_to: email,
          filter_spec: filterSpec,
        });
        toast.success({
          title: "Subscription created",
          description: `${describeCadence(cadence)} · ${email}`,
        });
      }
      onSaved(row);
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        const code =
          typeof err.body === "object" &&
          err.body !== null &&
          "error" in err.body
            ? String((err.body as { error: unknown }).error)
            : err.code ?? String(err.status);
        setError(code);
        handleApiError(err, {
          actionLabel: mode === "edit" ? "Update subscription" : "Create subscription",
        });
      } else {
        setError("unknown");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!submitting) onClose();
      }}
      title={mode === "edit" ? "Edit subscription" : "Subscribe to this report"}
      variant="detail"
      footer={
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {submitting
              ? "Saving…"
              : mode === "edit"
                ? "Save changes"
                : "Subscribe"}
          </Button>
        </>
      }
    >
      <div className="rb-audit__sub-modal">
        <label className="rb-audit__sub-label">Cadence</label>
        <div className="rb-audit__sub-radio-row" role="radiogroup" aria-label="Cadence">
          {CADENCE_OPTIONS.map((opt) => {
            const active = cadence === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                role="radio"
                aria-checked={active}
                className={
                  active
                    ? "rb-audit__sub-radio rb-audit__sub-radio--active"
                    : "rb-audit__sub-radio"
                }
                onClick={() => setCadence(opt.value)}
                disabled={submitting}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {mode === "create" && (
          <>
            <label className="rb-audit__sub-label">What to include</label>
            <div
              className="rb-audit__sub-radio-row"
              role="radiogroup"
              aria-label="What to include"
            >
              <button
                type="button"
                role="radio"
                aria-checked={include === "filtered"}
                className={
                  include === "filtered"
                    ? "rb-audit__sub-radio rb-audit__sub-radio--active"
                    : "rb-audit__sub-radio"
                }
                onClick={() => setInclude("filtered")}
                disabled={submitting}
              >
                Current filtered view
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={include === "all"}
                className={
                  include === "all"
                    ? "rb-audit__sub-radio rb-audit__sub-radio--active"
                    : "rb-audit__sub-radio"
                }
                onClick={() => setInclude("all")}
                disabled={submitting}
              >
                All events
              </button>
            </div>
          </>
        )}

        <label className="rb-audit__sub-label" htmlFor="rb-audit-sub-email">
          Deliver to
        </label>
        <input
          id="rb-audit-sub-email"
          type="email"
          className="rb-audit__sub-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
          aria-invalid={!emailValid}
        />

        <div className="rb-audit__sub-summary">
          You'll receive a <b>CSV attachment</b> {cadenceSummary} with events
          matching:
          <br />
          <span className="rb-audit__sub-summary-filters">{filterSummary}</span>
          <br />
          Delivered to <b>{email || "—"}</b>. Manage at{" "}
          <code className="mono mono--sm">/admin/audit/subscriptions</code>.
        </div>

        <div className="rb-audit__sub-deferred" role="note">
          Email delivery is being scheduled — the row is stored now; first
          runs land once the runner Lambda + SES sender are wired up
          (deferred from Phase 6).
        </div>

        {error !== null && (
          <div className="rb-audit__sub-error" role="alert">
            <code className="mono mono--sm">{error}</code> — failed to save.
          </div>
        )}
      </div>
    </Modal>
  );
}
